import { Action, createReducer, on } from '@ngrx/store';
import { GuestsActions } from '../actions';
import { TeamActions } from '@app/core/actions';


export const guestsFeatureKey = 'guests';

export interface State {
  guestsLoading: boolean;
  guestsError: string;
  emailsAuthorized: string[];
  patternsAuthorized: string[];
  emailDeleting: boolean;
  patternDeleting: boolean;

  guestDetails: any;
  guestDetailsLoading: boolean;
  guestDetailsLoadingError: any;

  guestDetailsUpdateProcessing: boolean;
  guestDetailsUpdateSuccess: boolean;
  guestDetailsUpdateProcessingError: any;

  configUpdating: boolean;
  configUpdated: boolean;
  configUpdateError: string;

  attachTeamProcessing: boolean;
  attachTeamSuccess: boolean;
  attachTeamError: any;

  detachTeamProcessing: boolean;
  detachTeamSuccess: boolean;
  detachTeamError: any;

  deleteGuestProcessing: boolean;
  deleteGuestSuccess: boolean;
  deleteGuestError: any;
}

export const initialState: State = {
  guestsLoading: false,
  guestsError: null,
  emailsAuthorized: [],
  patternsAuthorized: [],
  emailDeleting: false,
  patternDeleting: false,

  guestDetails: null,
  guestDetailsLoading: false,
  guestDetailsLoadingError: null,

  guestDetailsUpdateProcessing: false,
  guestDetailsUpdateSuccess: false,
  guestDetailsUpdateProcessingError: null,

  configUpdating: false,
  configUpdated: false,
  configUpdateError: null,

  attachTeamProcessing: false,
  attachTeamSuccess: false,
  attachTeamError: null,

  detachTeamProcessing: false,
  detachTeamSuccess: false,
  detachTeamError: null,

  deleteGuestProcessing: false,
  deleteGuestSuccess: false,
  deleteGuestError: null,
};

const guestsReducer = createReducer(
  initialState,
  // Load Guests
  on(GuestsActions.LoadGuests, (state) => ({
    ...state,
    ...initialState,
    guestsLoading: true,
  })),
  on(GuestsActions.LoadGuestsSuccess, (state, { emails }) => ({
    ...state,
    guestsLoading: false,
    emailsAuthorized: emails,
  })),
  on(GuestsActions.LoadGuestsFailure, (state, { error }) => ({
    ...state,
    guestsLoading: false,
    guestsError: error,
  })),
  // Load Guests Details
  on(GuestsActions.LoadGuestDetails, (state) => ({
    ...state,
    ...initialState,
    guestDetailsLoading: true,
  })),
  on(GuestsActions.LoadGuestDetailsSuccess, (state, { guest }) => ({
    ...state,
    guestDetails: guest,
    guestDetailsLoading: false,
  })),
  on(GuestsActions.LoadGuestDetailsFailure, (state, { error }) => ({
    ...state,
    guestDetailsLoading: false,
    guestDetailsLoadingError: error,
  })),
  // Update Guests Details
  on(GuestsActions.UpdateGuestDetails, (state) => ({
    ...state,
    ...initialState,
    guestDetailsUpdateProcessing: true,
  })),
  on(GuestsActions.UpdateGuestDetailsSuccess, (state, { guest }) => ({
    ...state,
    guestDetails: guest,
    guestDetailsUpdateProcessing: false,
    guestDetailsUpdateSuccess: true,
  })),
  on(GuestsActions.UpdateGuestDetailsFailure, (state, { error }) => ({
    ...state,
    guestDetailsUpdateProcessing: false,
    guestDetailsUpdateProcessingError: error,
  })),
  // Attach Guest to Team
  on(GuestsActions.AttachGuestToTeam, (state) => ({
    ...state,
    attachTeamProcessing: true,
    attachTeamSuccess: initialState.attachTeamSuccess,
    attachTeamError: initialState.attachTeamError,
  })),
  on(GuestsActions.AttachGuestToTeamSuccess, (state) => ({
    ...state,
    attachTeamProcessing: initialState.attachTeamProcessing,
    attachTeamSuccess: true,
  })),
  on(GuestsActions.AttachGuestToTeamFailure, (state, { error }) => ({
    ...state,
    attachTeamProcessing: initialState.attachTeamProcessing,
    attachTeamError: error,
  })),
  // Detach Guest to Team
  on(GuestsActions.DetachGuestFromTeam, (state) => ({
    ...state,
    detachTeamProcessing: true,
    detachTeamSuccess: initialState.detachTeamSuccess,
    detachTeamError: initialState.detachTeamError,
  })),
  on(GuestsActions.DetachGuestFromTeamSuccess, (state) => ({
    ...state,
    detachTeamProcessing: initialState.detachTeamProcessing,
    detachTeamSuccess: true,
  })),
  on(GuestsActions.DetachGuestFromTeamFailure, (state, { error }) => ({
    ...state,
    detachTeamProcessing: initialState.detachTeamProcessing,
    detachTeamError: error,
  })),
  // Delete Guest Globally
  on(GuestsActions.DeleteGuest, (state) => ({
    ...state,
    deleteGuestProcessing: true,
    deleteGuestSuccess: initialState.deleteGuestSuccess,
    deleteGuestError: initialState.deleteGuestError,
  })),
  on(GuestsActions.DeleteGuestSuccess, (state) => ({
    ...state,
    deleteGuestProcessing: false,
    deleteGuestSuccess: true,
  })),
  on(GuestsActions.DeleteGuestFailure, (state, { error }) => ({
    ...state,
    deleteGuestProcessing: false,
    deleteGuestError: error,
  })),
  // Add Guest Email
  on(GuestsActions.AddEmail, (state) => ({
    ...state,
    configUpdating: true,
    configUpdateError: initialState.configUpdateError,
    configUpdated: initialState.configUpdated,
  })),
  on(GuestsActions.AddEmailFailure, (state, { error }) => ({
    ...state,
    configUpdating: false,
    configUpdateError: error,
  })),
  // Add Guest Pattern
  on(GuestsActions.AddEmailPattern, (state) => ({
    ...state,
    configUpdating: true,
    configUpdateError: initialState.configUpdateError,
    configUpdated: initialState.configUpdated,
  })),
  on(GuestsActions.AddEmailPatternFailure, (state, { error }) => ({
    ...state,
    configUpdating: false,
    configUpdateError: error,
  })),
  // Update Team Config
  on(GuestsActions.UpdateTeamConfigSuccess, (state) => ({
    ...state,
    emailDeleting: initialState.emailDeleting,
    patternDeleting: initialState.patternDeleting,
    configUpdated: true,
  })),
  // Reset ConfigUpdateError
  on(GuestsActions.ResetError, (state) => ({
    ...state,
    configUpdateError: initialState.configUpdateError,
    attachTeamError: initialState.attachTeamError,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return guestsReducer(state, action);
}
